import * as React from "react";
import AceEditor from "react-ace";
import {Button, Icon, Modal, Table, Header, Input, Checkbox} from "semantic-ui-react";
import { workingConfig } from "../ConfigSignal";

// Ace Editor imports (add these if not globally available)
import "ace-builds/src-noconflict/mode-groovy";
import "ace-builds/src-noconflict/theme-monokai";

export interface ScriptSelectorModalProps {
    isOpen: boolean;
    onClose: () => void;
    scriptPath: string;
    setScriptPath: (path: string) => void;
    setScript: (script: string) => void;
}

type ScriptItem = {
    path: string;
    name: string;
    verboseName: string;
    description?: string;
    script: string;
};

type SortableColumn = keyof Pick<ScriptItem, 'path' | 'name' | 'verboseName' | 'description'>;

export function ScriptSelectorModal(props: ScriptSelectorModalProps) {
    const { isOpen, onClose, scriptPath, setScriptPath, setScript } = props;
    const [scriptsList, setScriptsList] = React.useState<Array<ScriptItem>>([]);
    const [selectedScriptInTable, setSelectedScriptInTable] = React.useState<string>("");
    const [previewModalOpen, setPreviewModalOpen] = React.useState(false);
    const [previewScript, setPreviewScript] = React.useState<ScriptItem | null>(null);
    const [searchTerm, setSearchTerm] = React.useState<string>("");

    const [sortColumn, setSortColumn] = React.useState<SortableColumn>('path');
    const [sortDirection, setSortDirection] = React.useState<'ascending' | 'descending'>('ascending');
    const [searchByMarked, setSearchByMarked] = React.useState<boolean>(false);

    React.useEffect(() => {
        if (workingConfig.value) {
            try {
                const config = JSON.parse(workingConfig.value);
                const scripts = findAllScripts(config);
                setScriptsList(scripts);
            } catch (error) {
                console.error("Error parsing config:", error);
            }
        }
    }, [workingConfig.value]);

    React.useEffect(() => {
        if (scriptPath) {
            setSelectedScriptInTable(scriptPath);
        }
    }, [scriptPath]);

    const findVerboseName = (config: any, scriptPathValue: string): string => {
        const pathParts = scriptPathValue.replace(/^\$\./, '').split('.');
        for (let i = 1; i <= 3; i++) {
            if (pathParts.length < i) break;
            const parentPathParts = pathParts.slice(0, pathParts.length - i);
            let parentObj = config;
            for (const part of parentPathParts) {
                if (!parentObj || typeof parentObj !== 'object') break;
                parentObj = parentObj[part];
            }
            if (parentObj && parentObj.verboseName) {
                return parentObj.verboseName;
            }
        }
        return "";
    };

    const findDescriptionName = (config: any, scriptPathValue: string): string => {
        const pathParts = scriptPathValue.replace(/^\$\./, '').split('.');
        for (let i = 1; i <= 3; i++) {
            if (pathParts.length < i) break;
            const parentPathParts = pathParts.slice(0, pathParts.length - i);
            let parentObj = config;
            for (const part of parentPathParts) {
                if (!parentObj || typeof parentObj !== 'object') break;
                parentObj = parentObj[part];
            }
            if (parentObj && parentObj.description) {
                return parentObj.description;
            }
        }
        return "";
    };

    const findAllScripts = (config: any) => {
        let results: Array<ScriptItem> = [];
        const findScriptsByKeyword = (obj: any, currentPathPrefix: string = "$") => {
            if (typeof obj === 'object' && obj !== null) {
                Object.keys(obj).forEach(key => {
                    const currentPath = currentPathPrefix === "$" ? `${currentPathPrefix}.${key}` : `${currentPathPrefix}.${key}`;
                    const value = obj[key];
                    if (typeof value === 'string' &&
                        (key.toLowerCase().includes('script') || currentPath.toLowerCase().includes('script'))) {
                        const verboseName = findVerboseName(config, currentPath);
                        results.push({
                            path: currentPath,
                            name: key,
                            verboseName: verboseName,
                            description: findDescriptionName(config, currentPath),
                            script: value
                        });
                    }
                    if (typeof value === 'object' && value !== null) {
                        findScriptsByKeyword(value, currentPath);
                    }
                });
            }
        };

        if( searchByMarked ) {

        } else {
            findScriptsByKeyword(config);
        }
        return results;
    };

    const handleScriptSelect = (selectedPath: string, scriptContent: string) => {
        setScriptPath(selectedPath);
        setScript(scriptContent);
        onClose();
    };

    const handlePreviewScript = (script: ScriptItem) => {
        setPreviewScript(script);
        setPreviewModalOpen(true);
    };

    const handleExportFromPreview = () => {
        if (previewScript) {
            setScriptPath(previewScript.path);
            setScript(previewScript.script);
            setPreviewModalOpen(false);
            onClose();
        }
    };

    const handleSort = (clickedColumn: SortableColumn) => {
        if (sortColumn !== clickedColumn) {
            setSortColumn(clickedColumn);
            setSortDirection('ascending');
        } else {
            setSortDirection(sortDirection === 'ascending' ? 'descending' : 'ascending');
        }
    };

    const sortedAndFilteredScriptsList = React.useMemo(() => {
        let displayList = [...scriptsList];

        if (searchTerm) {
            const lowercasedSearchTerm = searchTerm.toLowerCase();
            displayList = displayList.filter(script =>
                script.path.toLowerCase().includes(lowercasedSearchTerm) ||
                script.name.toLowerCase().includes(lowercasedSearchTerm) ||
                (script.verboseName && script.verboseName.toLowerCase().includes(lowercasedSearchTerm)) ||
                (script.description && script.description.toLowerCase().includes(lowercasedSearchTerm))
            );
        }

        if (sortColumn && sortDirection) {
            displayList.sort((a, b) => {
                const valA = (a[sortColumn] || "").toString().toLowerCase();
                const valB = (b[sortColumn] || "").toString().toLowerCase();

                if (valA < valB) {
                    return sortDirection === 'ascending' ? -1 : 1;
                }
                if (valA > valB) {
                    return sortDirection === 'ascending' ? 1 : -1;
                }
                return 0;
            });
        }
        return displayList;
    }, [scriptsList, searchTerm, sortColumn, sortDirection]);

    return (
        <>
            <Modal
                open={isOpen}
                onClose={onClose}
                size="fullscreen"
            >
                <Modal.Header>Browse Scripts</Modal.Header>
                <Modal.Content scrolling style={{ minHeight: '400px', display: 'flex', flexDirection: 'column' }}>
                    <p>search by keyword "script" -
                    <Checkbox
                        toggle
                        onChange={(e, { checked }) => {
                            setSearchByMarked(checked);
                        }}
                        />
                    - search by marked to open in Text-Modal</p>
                    <Input
                        icon={searchTerm ? <Icon name="times" link onClick={() => setSearchTerm('')} /> : 'search'}
                        placeholder="Search by path, name, or verbose name..."
                        fluid
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        style={{ marginBottom: '1em', flexShrink: 0 }}
                    />
                    {scriptsList.length === 0 ? (
                        <Header as="h3" textAlign="center" style={{ margin: '2em 0', flexGrow: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            No scripts found in the configuration.
                        </Header>
                    ) : sortedAndFilteredScriptsList.length === 0 ? (
                        <Header as="h3" textAlign="center" style={{ margin: '2em 0', flexGrow: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            No scripts match your search criteria.
                        </Header>
                    ) : (
                        <div style={{ overflowY: 'auto', flexGrow: 1 }}>
                            <Table celled selectable striped sortable>
                                <Table.Header>
                                    <Table.Row>
                                        <Table.HeaderCell
                                            width={8}
                                            sorted={sortColumn === 'path' ? sortDirection : undefined}
                                            onClick={() => handleSort('path')}
                                        >
                                            Script Path
                                        </Table.HeaderCell>
                                        <Table.HeaderCell
                                            width={3}
                                            sorted={sortColumn === 'name' ? sortDirection : undefined}
                                            onClick={() => handleSort('name')}
                                        >
                                            Script Name
                                        </Table.HeaderCell>
                                        <Table.HeaderCell
                                            width={3}
                                            sorted={sortColumn === 'verboseName' ? sortDirection : undefined}
                                            onClick={() => handleSort('verboseName')}
                                        >
                                            Verbose Name
                                        </Table.HeaderCell>
                                        <Table.HeaderCell
                                            width={3}
                                            sorted={sortColumn === 'description' ? sortDirection : undefined}
                                            onClick={() => handleSort('description')}
                                        >
                                            Description
                                        </Table.HeaderCell>
                                        <Table.HeaderCell width={2}>Actions</Table.HeaderCell>
                                    </Table.Row>
                                </Table.Header>
                                <Table.Body>
                                    {sortedAndFilteredScriptsList.map((script, index) => (
                                        <Table.Row// Using path for key is better if unique
                                            active={selectedScriptInTable === script.path}
                                            onClick={() => setSelectedScriptInTable(script.path)}
                                            style={{ cursor: 'pointer' }}
                                        >
                                            <Table.Cell>{script.path}</Table.Cell>
                                            <Table.Cell>{script.name}</Table.Cell>
                                            <Table.Cell>{script.verboseName}</Table.Cell>
                                            <Table.Cell>{script.description || ""}</Table.Cell>
                                            <Table.Cell>
                                                <div style={{ display: 'flex', gap: '5px' }}>
                                                    <Button
                                                        size="tiny"
                                                        primary
                                                        icon
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handlePreviewScript(script);
                                                        }}
                                                        title="Preview Script"
                                                    >
                                                        <Icon name="eye" /> Preview
                                                    </Button>
                                                    <Button
                                                        size="tiny"
                                                        color="green"
                                                        icon
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleScriptSelect(script.path, script.script);
                                                        }}
                                                        title="Select Script"
                                                    >
                                                        <Icon name="check" /> Select  </Button>
                                                </div>
                                            </Table.Cell>
                                        </Table.Row>
                                    ))}
                                </Table.Body>
                            </Table>
                        </div>
                    )}
                </Modal.Content>
                <Modal.Actions>
                    <Button color="red" onClick={onClose}>
                        <Icon name="cancel" /> Close
                    </Button>
                </Modal.Actions>
            </Modal>

            <Modal
                open={previewModalOpen}
                onClose={() => setPreviewModalOpen(false)}
                size="large"
            >
                <Modal.Header>
                    Preview Script: {previewScript?.name || ""}
                    {previewScript?.verboseName && ` (${previewScript.verboseName})`}
                </Modal.Header>
                <Modal.Content scrolling>
                    <Header as="h4">Path: {previewScript?.path || ""}</Header>
                    <AceEditor
                        theme="monokai"
                        mode="groovy"
                        style={{ width: "100%", height: "50vh" }}
                        value={previewScript?.script || ""}
                        readOnly={true}
                    />
                </Modal.Content>
                <Modal.Actions>
                    <Button color="red" onClick={() => setPreviewModalOpen(false)}>
                        <Icon name="cancel" /> Close
                    </Button>
                    <Button
                        primary
                        onClick={handleExportFromPreview}
                        disabled={!previewScript}
                    >
                        <Icon name="download" /> Export
                    </Button>
                </Modal.Actions>
            </Modal>
        </>
    );
}